import {
  useApolloClient,
  useMutation as useApolloMutation
} from "@apollo/client/react/hooks"
import { useTranslation } from "react-i18next"

import { callMutation } from "shared/api/callMutation"
import { evictProjectPrice } from "shared/api/price/evictPriceCache"
import { MutationSchema } from "shared/api/types"
import { emitAppEvent } from "shared/lib/appEventEmitter"
import { useProjectRequestMonitor } from "shared/lib/request-monitoring/useProjectRequestMonitor"
import { useEvent } from "shared/lib/useEvent"
import { useRequestToasts } from "shared/lib/useRequestToasts"
import { ApolloFetchPolicy } from "shared/types/ApolloFetchPolicy"
import { MOVE_PROJECT_CHILD } from "shared/types/AppEvent"
import { RoomTypename } from "shared/types/Project"
import { UPDATE_ROOM } from "shared/types/ProjectMutations"
import { Room } from "shared/types/Room"
import {
  RoomSchema,
  UpdateRoomInputSchema
} from "shared/types/__generated__/validation"

import { didMove } from "entities/project-children/lib/didMove"
import { updateProjectChildrenSorting } from "entities/project-children/lib/updateProjectChildrenSorting"
import { evictProjectHistory } from "entities/projects/graphql/project-history/evictProjectHistory"
import { useProjectChildrenLazyQuery } from "entities/projects/graphql/queries.generated"
import {
  UpdateRoomDocument,
  UpdateRoomMutation,
  UpdateRoomMutationVariables
} from "entities/rooms/graphql/mutations.generated"
import z from "zod"

import { prepareRoomUpdate } from "../model/prepareRoomUpdate"
import { RoomUpdate } from "../types/Room"

const UpdateRoomVariablesSchema = z.object({
  id: z.string(),
  input: UpdateRoomInputSchema()
})

export function useUpdateRoom() {
  const { t } = useTranslation()
  const { cache: apolloCache } = useApolloClient()
  const [fetchProject] = useProjectChildrenLazyQuery({
    fetchPolicy: ApolloFetchPolicy.NETWORK_ONLY
  })
  const { raiseLoadingToast } = useRequestToasts({
    messages: {
      loading: t("Request.Room.UPDATE_ROOM.Start"),
      success: t("Request.Room.UPDATE_ROOM.Success"),
      failure: t("Request.Room.UPDATE_ROOM.Failure")
    }
  })
  const startProjectRequest = useProjectRequestMonitor()

  const [apolloMutate, rest] = useApolloMutation<
    UpdateRoomMutation,
    UpdateRoomMutationVariables
  >(UpdateRoomDocument)

  const updateRoom = useEvent(
    ({
      projectID,
      parentID,
      displayPosition,
      room
    }: {
      projectID: string
      parentID?: string | null
      displayPosition?: number
      room: RoomUpdate
    }) => {
      const { id } = room

      const { changedValues, input, children, optimisticResponse, moveData } =
        prepareRoomUpdate({
          room,
          displayPosition,
          cache: apolloCache,
          projectID,
          parentID,
          childType: "Room"
        })

      if (Object.keys(changedValues).length === 0 && !didMove(moveData))
        return undefined

      const { raiseSuccessToast, raiseErrorToast } = raiseLoadingToast()
      const completeProjectRequest = startProjectRequest({
        projectID,
        requestType: UPDATE_ROOM
      })

      if (didMove(moveData)) {
        emitAppEvent(MOVE_PROJECT_CHILD, {
          id: room.id,
          parentID: moveData.nextParentID,
          parentType: moveData.nextParentType,
          position: moveData.nextPosition,
          type: RoomTypename,
          projectID
        })
      }

      return callMutation(apolloMutate, {
        variables: {
          id,
          input: didMove(moveData)
            ? {
                ...input,
                position: moveData.nextPosition,
                parent: {
                  id: moveData.nextParentID,
                  type: moveData.nextParentType
                }
              }
            : input
        },
        transform: data => data.room,
        schemas: {
          input: UpdateRoomVariablesSchema,
          response: MutationSchema.extend({ room: RoomSchema() })
        },
        apolloOptions: {
          optimisticResponse,
          update: (cache, result) => {
            if (didMove(moveData)) {
              const roomResult: Room | null = result.data?.room
                ? { ...result.data.room, children }
                : null
              if (roomResult && didMove(moveData)) {
                updateProjectChildrenSorting({
                  movedChild: roomResult,
                  prevParentID: moveData.prevParentID,
                  prevParentType: moveData.prevParentType,
                  nextParentID: moveData.nextParentID,
                  nextParentType: moveData.nextParentType,
                  cache
                })
              }
            }
          },
          onCompleted: () => {
            if (moveData) {
              evictProjectPrice(projectID, apolloCache, true)
            }
            evictProjectHistory(projectID, apolloCache)
            fetchProject({ variables: { id: projectID } })
            completeProjectRequest()
            raiseSuccessToast()
          },
          onError: () => {
            if (moveData) {
              evictProjectPrice(projectID, apolloCache, true)
            }
            fetchProject({ variables: { id: projectID } })
            completeProjectRequest()
            raiseErrorToast()
          }
        }
      })
    }
  )

  return [updateRoom, rest] as [typeof updateRoom, typeof rest]
}
